// Shared POST /picking/wave/start wrapper for anchor-seeded Large
// waves (one order per wave). Maps the BE's response codes onto the
// AnchorScanOutcome vocabulary LargeAnchorScan renders feedback for.
// Used by PickTypeSelector (first order) and ActiveWaveScreen's
// next-order phase (every subsequent order) so the two entry points
// can never drift.
import { apiFetch } from "../../api/apiFetch";
import type { AnchorScanOutcome } from "./LargeAnchorScan";

export interface LargeStartOptions {
    tier: string;
    ageFilter: string;
    deliveryFilter: string;
    channelFilter: string;
    ageFrom: string | null;
    ageTo: string | null;
    // "Must ship today" clock filter (see dispatchDue.ts). Optional so
    // older callers behave as before (off).
    dueFilter?: boolean;
    pickQueue?: number | null;
    /** 0-4, from the york-fill prompt. Omit on the first order of a
     *  session — the picker is not asked, and the BE treats absent as
     *  an empty york (no capacity gate). 4 never reaches here: a full
     *  york goes straight to the drop-off screen. */
    yorkFillQuarters?: number | null;
    /** Pick Advisor: what the queue recommended and whether this start
     *  followed it — recorded on the wave.start event. Optional. */
    advisor?: { rule: string; recommended: string; followed: boolean; suggested_only: boolean };
}

export const startLargeWaveAt = async (
    seedBarcode: string,
    opts: LargeStartOptions,
    t: (key: string, vars?: Record<string, string>) => string,
): Promise<AnchorScanOutcome> => {
    let res: Response;
    try {
        res = await apiFetch("/picking/wave/start", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                pickType: "large",
                tier: opts.tier,
                ageFilter: opts.ageFilter,
                deliveryFilter: opts.deliveryFilter,
                channelFilter: opts.channelFilter,
                ageFrom: opts.ageFrom || null,
                ageTo: opts.ageTo || null,
                dueFilter: opts.dueFilter === true,
                pickQueue: opts.pickQueue ?? null,
                yorkFillQuarters: opts.yorkFillQuarters ?? null,
                seedBarcode,
                ...(opts.advisor ? { advisor: opts.advisor } : {}),
            }),
        });
    } catch {
        return { kind: "failed" };
    }
    if (res.ok) return { kind: "started" };
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const code = body?.code as string | undefined;
    // The picker already holds an active wave (double-tap, or a stale
    // screen after a resume elsewhere) — treat as started: the parent's
    // refetch mounts whatever wave the server says is theirs.
    if (code === "ALREADY_ACTIVE") return { kind: "started" };
    // Orders are queued, none fit the space left. Distinct from
    // no_orders so the picker is sent to the drop zone rather than back
    // to the selector.
    if (code === "YORK_FULL") return { kind: "york_full" };
    if (code === "NO_CANDIDATES" || code === "NO_MATCHING" || code === "NO_SELECTED") {
        return { kind: "no_orders" };
    }
    if (code === "WRONG_WAREHOUSE") {
        return {
            kind: "rejected",
            message: t("scan_wrong_warehouse", { warehouse: String(body?.locationWarehouse ?? "?") }),
        };
    }
    if (code === "INVALID_STARTING_LOCATION") return { kind: "rejected" };
    return { kind: "failed" };
};
